import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Sparkles, ArrowRight, Menu, X, BarChart3, Users, Shield, CheckCircle2, Hexagon, Infinity as InfinityIcon, Triangle, Zap, Feather, Box, Mic, MicOff } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Helmet } from 'react-helmet-async';
import customLogo from '@/assets/favicon-removebg-preview.png';

const Landing = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [intent, setIntent] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);

  const featuresList = [
    {
      num: '01',
      tag: '01 Build AI',
      icon: Sparkles,
      title: 'Build forms with AI',
      desc: 'Describe what you need. AI builds the form fields, logic, and structure included.'
    },
    {
      num: '02',
      tag: '02 Collect',
      icon: ArrowRight,
      title: 'Collect better business data',
      desc: 'Leads, feedback, applications, onboarding collect what actually matters.'
    },
    {
      num: '03',
      tag: '03 Understand',
      icon: BarChart3,
      title: 'Understand your responses',
      desc: 'Analytics surface patterns and signals not just a list of submissions.'
    },
    {
      num: '04',
      tag: '04 Contacts',
      icon: Users,
      title: 'Turn submissions into contacts',
      desc: 'Every response becomes a clean contact record. No CSV exports needed.'
    },
    {
      num: '05',
      tag: '05 Action',
      icon: CheckCircle2,
      title: 'Take action on insights',
      desc: 'Connect via webhooks and integrations. Know what to do next.'
    },
    {
      num: '06',
      tag: '06 Security',
      icon: Shield,
      title: 'Secure by default',
      desc: 'AES-256 encryption and role-based access. Your data stays yours.'
    }
  ];

  const scrollToFeature = (index: number) => {
    setActiveFeature(index);
    const el = document.getElementById(`feature-card-${index}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  };
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [isListening, setIsListening] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  const faqs = [
    {
      q: 'Do I really just describe the form to build it?',
      a: 'Yes. You type "I need a client intake form that asks for company details and budget." Our AI instantly creates the layout, inputs, and validation rules. No dragging and dropping required.'
    },
    {
      q: 'What happens when someone actually fills out my form?',
      a: 'Instead of just sending you an email or adding a row to a spreadsheet, Bravax Forms automatically creates an intelligent contact record. Every submission becomes part of a built-in CRM that tracks relationships over time.'
    },
    {
      q: 'How does it help me "understand" my data?',
      a: 'Our analytics engine doesn\'t just count submissions. It surfaces patterns—like identifying common themes in customer feedback or flagging high-value leads based on their responses. You get insights, not just raw data.'
    },
    {
      q: 'I already use a CRM. Can I connect Bravax Forms to it?',
      a: 'Absolutely. While we offer built-in contact management out of the box, we integrate seamlessly via webhooks and native API connections to push your processed data wherever it needs to go.'
    },
    {
      q: 'Is this secure enough for business data?',
      a: 'Enterprise-grade from day one. Data is encrypted at rest (AES-256), and your submissions are never used to train our AI models. You have complete control over data retention and role-based access.'
    }
  ];
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleBuild = () => {
    const trimmedIntent = intent.trim();
    if (trimmedIntent) {
      sessionStorage.setItem('pending_intent', trimmedIntent);
    }

    if (user) {
      navigate(trimmedIntent ? `/ai-builder?intent=${encodeURIComponent(trimmedIntent)}` : '/ai-builder');
      return;
    }

    // Check if returning user with existing account
    const hasAccount = localStorage.getItem('bravax_has_account') === 'true';

    if (hasAccount) {
      // Returning user -> skip signup, go to login
      navigate(trimmedIntent ? `/auth/login?intent=${encodeURIComponent(trimmedIntent)}` : '/auth/login');
    } else {
      // New user -> go to signup
      navigate(trimmedIntent ? `/auth/signup?intent=${encodeURIComponent(trimmedIntent)}` : '/auth/signup');
    }
  };
  const toggleVoiceInput = () => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      toast({
        title: "Voice input not supported",
        description: "Your browser doesn't support Web Speech API. Try Chrome, Edge, or Safari.",
        variant: "destructive"
      });
      return;
    }

    if (isListening) {
      setIsListening(false);
      return;
    }

    try {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        setIsListening(true);
        toast({ title: "Listening...", description: "Speak your form requirements clearly." });
      };

      recognition.onresult = (event: any) => {
        const transcript = Array.from(event.results)
          .map((result: any) => result[0].transcript)
          .join('');
        setIntent(transcript);
      };

      recognition.onerror = (event: any) => {
        console.error("Speech recognition error:", event.error);
        setIsListening(false);
        toast({ title: "Voice error", description: "Failed to capture audio. Please try again.", variant: "destructive" });
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognition.start();
    } catch (e) {
      console.error(e);
      setIsListening(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground font-sans selection:bg-primary/30 selection:text-primary-foreground overflow-x-hidden">
      <Helmet>
        <title>AI Form Builder for Smarter Data Collection | Bravax Forms</title>
        <meta name="description" content="Create forms with AI, collect responses, understand your data, and turn submissions into actionable leads and insights. Bravax Forms — built for businesses that do more with their data." />
      </Helmet>

      {/* Navigation */}
      <div className="fixed top-0 left-0 right-0 z-50 flex justify-center pt-3 sm:pt-6 px-3 sm:px-4 transition-all duration-300 pointer-events-none">
        <nav className="w-full max-w-5xl rounded-2xl sm:rounded-full transition-all duration-300 flex justify-between items-center px-4 sm:px-6 py-3 sm:py-4 pointer-events-auto bg-card/80 backdrop-blur-xl shadow-2xl border border-white/10">
          <div className="flex items-center justify-between w-full lg:w-auto gap-4 lg:gap-10">
            <div className="flex items-center gap-3 cursor-pointer opacity-90 hover:opacity-100 transition-opacity" onClick={() => navigate('/')}>
              <img src={customLogo} alt="Bravax Forms" className="h-7 sm:h-8 w-auto object-contain" />
              <span className="text-lg sm:text-xl font-sans font-bold text-white tracking-normal leading-none pt-0.5 sm:pt-1">Bravax Forms</span>
            </div>

            {/* Desktop Navigation Links */}
            <div className="hidden lg:flex items-center gap-10 text-base font-medium text-muted-foreground">
              {/* Features Dropdown */}
              <div className="relative group py-2">
                <button className="flex items-center gap-1 hover:text-foreground transition-colors focus:outline-none">
                  Features
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-70 group-hover:opacity-100 group-hover:rotate-180 transition-transform duration-200"><path d="m6 9 6 6 6-6" /></svg>
                </button>

                {/* Dropdown Menu */}
                <div className="absolute top-full left-0 pt-4 opacity-0 translate-y-2 pointer-events-none group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto transition-all duration-200">
                  <div className="bg-card rounded-2xl shadow-2xl p-6 flex gap-10 whitespace-nowrap border border-white/10">
                    {/* Column 1 */}
                    <div className="flex flex-col gap-3">
                      <a href="#features" className="text-primary hover:text-primary/80 transition-colors">Form Builder</a>
                      <a href="#features" className="hover:text-primary transition-colors">AI Generation</a>
                      <a href="#features" className="hover:text-primary transition-colors">Conditional Logic</a>
                      <a href="#features" className="hover:text-primary transition-colors">Multi-step Forms</a>
                      <a href="#features" className="hover:text-primary transition-colors">Custom CSS</a>
                    </div>
                    {/* Column 2 */}
                    <div className="flex flex-col gap-3">
                      <a href="#features" className="hover:text-primary transition-colors">Real-time Analytics</a>
                      <a href="#features" className="hover:text-primary transition-colors">Built-in CRM</a>
                      <a href="#features" className="hover:text-primary transition-colors">Email Notifications</a>
                      <a href="/api-docs" className="hover:text-primary transition-colors">Webhooks & API</a>
                      <a href="#features" className="hover:text-primary transition-colors">Data Security</a>
                    </div>
                    {/* Column 3 */}
                    <div className="flex flex-col gap-3">
                      <a href="#features" className="hover:text-primary transition-colors">Enterprise Security</a>
                      <a href="#features" className="hover:text-primary transition-colors">Custom Domains</a>
                      <a href="#features" className="hover:text-primary transition-colors">Team Workspaces</a>
                      <a href="#features" className="hover:text-primary transition-colors">Audit Logs</a>
                    </div>
                  </div>
                </div>
              </div>

              <a href="#features" className="hover:text-foreground transition-colors">Solutions</a>
              <a href="#pricing" className="hover:text-foreground transition-colors">Pricing</a>
              <a href="#faq" className="hover:text-foreground transition-colors">FAQ</a>
            </div>
          </div>

          {/* Right Action Buttons - Desktop */}
          <div className="hidden lg:flex items-center gap-4">
            <Button variant="ghost" className="text-muted-foreground hover:text-foreground font-medium" onClick={() => navigate('/auth/login')}>
              Log in
            </Button>
            <Button className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-6 shadow-[0_0_20px_rgba(0,210,180,0.3)] transition-all hover:scale-105" onClick={() => navigate('/auth/signup')}>
              Start Building
            </Button>
          </div>

          {/* Mobile Hamburger Menu Button */}
          <div className="flex lg:hidden items-center gap-2">
            <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 rounded-full" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </nav>
      </div>

      {/* Mobile Navigation Drawer */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 bg-background/95 backdrop-blur-2xl lg:hidden pt-24 px-6 pb-10 flex flex-col justify-between animate-in fade-in duration-200">
          <div className="flex flex-col gap-5 pt-4">
            <a href="#features" onClick={() => setMobileMenuOpen(false)} className="text-lg font-medium text-white hover:text-primary transition-colors py-3 border-b border-white/10">Features & Solutions</a>
            <a href="#pricing" onClick={() => setMobileMenuOpen(false)} className="text-lg font-medium text-white hover:text-primary transition-colors py-3 border-b border-white/10">Pricing</a>
            <a href="#faq" onClick={() => setMobileMenuOpen(false)} className="text-lg font-medium text-white hover:text-primary transition-colors py-3 border-b border-white/10">FAQ</a>
          </div>
          <div className="flex flex-col gap-3 pt-6 border-t border-white/10">
            <Button variant="outline" className="w-full h-12 rounded-xl text-white border-white/20 hover:bg-white/10 text-base font-semibold" onClick={() => { setMobileMenuOpen(false); navigate('/auth/login'); }}>
              Log in
            </Button>
            <Button className="w-full h-12 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground text-base font-semibold shadow-lg" onClick={() => { setMobileMenuOpen(false); navigate('/auth/signup'); }}>
              Start Building
            </Button>
          </div>
        </div>
      )}

      <main className="relative z-10 pt-24 sm:pt-32">
        {/* Hero Section */}
        <section className="pt-6 sm:pt-16 pb-12 sm:pb-24 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto text-center">

          <h1 className="text-3xl xs:text-4xl sm:text-6xl md:text-[5.5rem] font-display font-medium tracking-normal mb-6 sm:mb-8 mx-auto leading-tight sm:leading-[1.08] text-white">
            Create smarter forms.<br className="hidden sm:inline" />
            <span className="text-white/60 italic block sm:inline mt-1 sm:mt-0">Turn responses into action.</span>
          </h1>

          <p className="text-sm sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8 sm:mb-16 leading-relaxed px-2 sm:px-0">
            Describe what you need to collect. Bravax Forms builds the form, gathers responses, surfaces the insights, and organises your contacts all from one place.
          </p>

          {/* ChatGPT-style Floating AI Input Box */}
          <div className="max-w-3xl mx-auto group relative mb-12 sm:mb-24 text-left">
            {/* Glow accent */}
            <div className="absolute -inset-1 bg-gradient-to-r from-primary/30 to-purple-500/20 rounded-[1.5rem] sm:rounded-[2.5rem] blur-xl sm:blur-2xl opacity-50 group-hover:opacity-80 transition duration-500 pointer-events-none" />
            
            {/* Input Bar Container */}
            <div className="relative rounded-[1.25rem] sm:rounded-[2rem] bg-card/70 border border-white/15 p-3.5 sm:p-6 shadow-2xl backdrop-blur-2xl transition-all focus-within:border-primary/50 focus-within:ring-1 focus-within:ring-primary/30 flex flex-col justify-between gap-3 sm:gap-4">
              <Textarea
                value={intent}
                onChange={(e) => setIntent(e.target.value)}
                placeholder="Ask Bravax AI to build a form... (e.g. Create a client onboarding form with budget range and automated CRM sync)"
                className="w-full min-h-[75px] sm:min-h-[110px] bg-transparent border-0 resize-none text-sm sm:text-base md:text-lg text-white placeholder:text-white/35 focus-visible:ring-0 p-1 sm:p-2 leading-relaxed"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleBuild();
                  }
                }}
              />

              {/* Bottom Row */}
              <div className="flex items-center justify-between gap-3 pt-2 border-t border-white/5">
                {/* Suggestion Chips - Horizontally scrollable on mobile */}
                <div className="flex items-center gap-2 overflow-x-auto no-scrollbar min-w-0 flex-1">
                  {[
                    {
                      label: "⚡ Client Intake",
                      prompt: "Build a client intake form asking for company name, project scope, budget range, and target completion timeline."
                    },
                    {
                      label: "🚀 SaaS Onboarding",
                      prompt: "Create a SaaS onboarding survey collecting user role, primary goal, team size, and feature preferences."
                    },
                    {
                      label: "💬 Customer Feedback",
                      prompt: "Create a customer feedback form with a 1-5 rating scale, key highlight field, and WhatsApp notification routing."
                    }
                  ].map((sample) => (
                    <button
                      key={sample.label}
                      type="button"
                      onClick={() => setIntent(sample.prompt)}
                      className="text-xs bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 hover:text-white px-3 py-1.5 rounded-full transition-all text-left font-medium whitespace-nowrap shrink-0"
                    >
                      {sample.label}
                    </button>
                  ))}
                </div>

                {/* Mic & Submit Action Buttons */}
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    type="button"
                    onClick={toggleVoiceInput}
                    variant="ghost"
                    size="icon"
                    className={`rounded-full w-10 h-10 sm:w-11 sm:h-11 transition-all ${
                      isListening
                        ? 'bg-red-500/20 text-red-400 animate-pulse border border-red-500/30'
                        : 'text-white/60 hover:text-white hover:bg-white/10'
                    }`}
                    title={isListening ? "Stop listening" : "Voice input (Speech to Text)"}
                  >
                    {isListening ? <MicOff className="h-4 w-4 sm:h-5 sm:w-5" /> : <Mic className="h-4 w-4 sm:h-5 sm:w-5" />}
                  </Button>

                  <Button
                    type="button"
                    onClick={handleBuild}
                    size="icon"
                    className="rounded-full w-10 h-10 sm:w-11 sm:h-11 bg-primary hover:bg-primary/90 text-primary-foreground shadow-[0_0_20px_rgba(0,210,180,0.4)] shrink-0 transition-transform hover:scale-105"
                    title="Generate Form"
                  >
                    <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Trusted By Section (Marquee) */}
        <section className="py-8 sm:py-12 relative z-10 bg-background overflow-hidden">
          <div className="text-center mb-6 sm:mb-10 px-4">
            <p className="text-sm sm:text-base md:text-xl text-muted-foreground font-medium">Trusted by businesses collecting smarter data every day</p>
          </div>

          <div className="relative flex overflow-hidden group">
            <div className="absolute top-0 left-0 h-full w-16 sm:w-32 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
            <div className="absolute top-0 right-0 h-full w-16 sm:w-32 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />

            {/* Sliding Track */}
            <div className="flex w-max animate-slide group-hover:[animation-play-state:paused]">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="flex items-center gap-6 sm:gap-12 px-6 sm:px-12">
                  <div className="flex items-center gap-2.5 sm:gap-3 text-white/50 hover:text-white/90 transition-colors grayscale hover:grayscale-0">
                    <Hexagon className="h-6 w-6 sm:h-8 sm:w-8 text-[#FF4F00]" strokeWidth={2} />
                    <span className="text-lg sm:text-xl font-sans font-bold tracking-normal">Nexora</span>
                  </div>
                  <div className="flex items-center gap-2.5 sm:gap-3 text-white/50 hover:text-white/90 transition-colors grayscale hover:grayscale-0">
                    <Sparkles className="h-6 w-6 sm:h-8 sm:w-8 text-[#00D2B4]" strokeWidth={2} />
                    <span className="text-lg sm:text-xl font-sans font-bold tracking-normal">Fluxenta</span>
                  </div>
                  <div className="flex items-center gap-2.5 sm:gap-3 text-white/50 hover:text-white/90 transition-colors grayscale hover:grayscale-0">
                    <Triangle className="h-6 w-6 sm:h-8 sm:w-8 text-blue-500" strokeWidth={2} />
                    <span className="text-lg sm:text-xl font-sans font-bold tracking-normal">Veltriq</span>
                  </div>
                  <div className="flex items-center gap-2.5 sm:gap-3 text-white/50 hover:text-white/90 transition-colors grayscale hover:grayscale-0">
                    <InfinityIcon className="h-6 w-6 sm:h-8 sm:w-8 text-orange-500" strokeWidth={2} />
                    <span className="text-lg sm:text-xl font-sans font-bold tracking-normal">Fyntra</span>
                  </div>
                  <div className="flex items-center gap-2.5 sm:gap-3 text-white/50 hover:text-white/90 transition-colors grayscale hover:grayscale-0">
                    <Zap className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-500" strokeWidth={2} />
                    <span className="text-lg sm:text-xl font-sans font-bold tracking-normal">Synthetix</span>
                  </div>
                  <div className="flex items-center gap-2.5 sm:gap-3 text-white/50 hover:text-white/90 transition-colors grayscale hover:grayscale-0">
                    <Feather className="h-6 w-6 sm:h-8 sm:w-8 text-pink-500" strokeWidth={2} />
                    <span className="text-lg sm:text-xl font-sans font-bold tracking-normal">Lumina</span>
                  </div>
                  <div className="flex items-center gap-2.5 sm:gap-3 text-white/50 hover:text-white/90 transition-colors grayscale hover:grayscale-0">
                    <Box className="h-6 w-6 sm:h-8 sm:w-8 text-purple-500" strokeWidth={2} />
                    <span className="text-lg sm:text-xl font-sans font-bold tracking-normal">Quantum</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features - Grid Layout */}
        <section id="features" className="scroll-mt-24 py-10 sm:py-16 px-3 sm:px-6 lg:px-8 max-w-[90rem] mx-auto relative z-10">
          <div className="bg-surface-lowest rounded-[1.5rem] sm:rounded-[2.5rem] p-5 sm:p-12 lg:p-20 relative overflow-hidden shadow-2xl">
            {/* Subtle side accents */}
            <div className="absolute top-24 -left-px w-px h-32 bg-gradient-to-b from-transparent via-primary/50 to-transparent" />
            <div className="absolute top-24 -right-px w-px h-32 bg-gradient-to-b from-transparent via-primary/50 to-transparent" />

            <div className="flex flex-col items-center text-center mb-8 sm:mb-12 relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 sm:px-5 sm:py-2 rounded-full bg-surface-high mb-6 sm:mb-8">
                <Sparkles className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white/70" />
                <span className="text-xs sm:text-sm text-white/90 font-medium">Everything you need</span>
              </div>
              <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-medium text-white mb-4 sm:mb-6 tracking-normal leading-tight">
                From form creation<br className="hidden sm:inline" /> to business insight.
              </h2>
              <p className="text-sm sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed mb-6">
                Most form builders stop at the response. Bravax Forms is built around what happens next, understanding your data, organising your contacts, and taking action.
              </p>
              
              {/* Mobile Quick-Jump Pills (visible < md) */}
              <div className="flex md:hidden items-center gap-2 overflow-x-auto no-scrollbar w-full pb-2 mb-4 shrink-0 px-1">
                {featuresList.map((item, idx) => (
                  <button
                    key={item.num}
                    type="button"
                    onClick={() => scrollToFeature(idx)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-all whitespace-nowrap shrink-0 font-medium ${
                      activeFeature === idx
                        ? 'bg-primary text-primary-foreground border-primary shadow-[0_0_15px_rgba(0,210,180,0.4)]'
                        : 'bg-white/5 border-white/10 text-white/70 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {item.tag}
                  </button>
                ))}
              </div>

              {/* Features Container (Carousel on mobile < md, Grid on desktop >= md) */}
              <div
                className="flex md:grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 text-left relative z-10 w-full overflow-x-auto md:overflow-x-visible snap-x snap-mandatory no-scrollbar pb-4 md:pb-0 px-1 md:px-0 mt-2 sm:mt-8"
                onScroll={(e) => {
                  const target = e.currentTarget;
                  const scrollLeft = target.scrollLeft;
                  const cardWidth = target.firstElementChild?.clientWidth || 280;
                  const newIndex = Math.round(scrollLeft / (cardWidth + 16));
                  if (newIndex >= 0 && newIndex < featuresList.length && newIndex !== activeFeature) {
                    setActiveFeature(newIndex);
                  }
                }}
              >
                {featuresList.map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.num}
                      id={`feature-card-${idx}`}
                      className="bg-card/50 rounded-2xl sm:rounded-3xl p-5 sm:p-8 lg:p-10 hover:bg-card transition-colors shadow-lg group shrink-0 w-[84vw] max-w-[320px] md:w-auto md:max-w-none snap-center flex flex-col relative border border-white/10 md:border-white/5"
                    >
                      <div className="flex items-center justify-between mb-4 sm:mb-8">
                        <Icon className="h-8 w-8 sm:h-10 sm:w-10 text-primary group-hover:scale-110 transition-transform duration-300" strokeWidth={1.5} />
                        <span className="text-xs font-mono font-bold text-primary/80 bg-primary/10 border border-primary/20 px-2.5 py-1 rounded-full">
                          {item.num}
                        </span>
                      </div>
                      <div>
                        <h3 className="text-base sm:text-lg font-sans font-semibold text-white mb-2 sm:mb-3">{item.title}</h3>
                        <p className="text-muted-foreground text-xs sm:text-sm leading-relaxed">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Mobile Carousel Indicators (visible < md) */}
              <div className="flex md:hidden items-center justify-center gap-1.5 mt-3">
                {featuresList.map((_, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => scrollToFeature(idx)}
                    className={`h-1.5 rounded-full transition-all ${
                      activeFeature === idx ? 'w-6 bg-primary' : 'w-1.5 bg-white/20'
                    }`}
                    aria-label={`Go to feature ${idx + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="scroll-mt-24 py-10 sm:py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto relative z-10">
          <div className="flex flex-col items-center text-center mb-10 sm:mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 sm:px-5 sm:py-2 rounded-full bg-card mb-6 sm:mb-8">
              <BarChart3 className="h-4 w-4 text-white/70" />
              <span className="text-xs sm:text-sm text-white/90 font-medium">Pricing</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-6xl font-display font-medium text-white mb-3 sm:mb-4 tracking-normal">Simple, transparent pricing.</h2>
            <p className="text-sm sm:text-lg text-muted-foreground max-w-xl mx-auto">Start free. Upgrade as your data collection grows. No hidden fees.</p>
          </div>

          {/* 3 Main Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch mb-8 sm:mb-12">
            
            {/* Free Plan */}
            <div className="bg-card/50 border border-white/10 rounded-2xl sm:rounded-3xl p-5 sm:p-8 lg:p-10 flex flex-col justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium mb-2 sm:mb-3">Free</p>
                <div className="flex items-end gap-2 mb-2">
                  <span className="text-3xl sm:text-5xl font-display font-normal text-white tracking-normal">Free</span>
                </div>
                <p className="text-xs text-muted-foreground mb-6 sm:mb-8">Perfect for getting started. Forever free.</p>

                <ul className="space-y-2.5 sm:space-y-3 text-xs sm:text-sm text-muted-foreground">
                  <li className="flex items-center gap-2.5 sm:gap-3">
                    <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                    <span>Up to <strong>2 forms</strong></span>
                  </li>
                  <li className="flex items-center gap-2.5 sm:gap-3">
                    <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                    <span><strong>50 submissions</strong> / month</span>
                  </li>
                  <li className="flex items-center gap-2.5 sm:gap-3">
                    <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                    <span>Basic analytics</span>
                  </li>
                  <li className="flex items-center gap-2.5 sm:gap-3">
                    <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                    <span>Multi-channel delivery</span>
                  </li>
                  <li className="flex items-center gap-2.5 sm:gap-3">
                    <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                    <span>Email support</span>
                  </li>
                </ul>
              </div>

              <Button
                variant="outline"
                className="w-full h-11 sm:h-12 rounded-xl sm:rounded-2xl border-white/10 text-white hover:bg-white/5 font-semibold mt-6 sm:mt-8 text-sm sm:text-base"
                onClick={() => navigate('/auth/signup')}
              >
                Get Started Free
              </Button>
            </div>

            {/* Starter Plan — Highlighted */}
            <div className="bg-primary/10 border border-primary/40 rounded-2xl sm:rounded-3xl p-5 sm:p-8 lg:p-10 flex flex-col justify-between relative overflow-hidden shadow-2xl">
              <div className="inline-block self-start sm:self-auto sm:absolute sm:top-5 sm:right-5 bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-3 sm:mb-0">
                Most Popular
              </div>

              <div>
                <p className="text-xs uppercase tracking-widest text-primary font-medium mb-2 sm:mb-3">Starter</p>
                <div className="flex items-end gap-1.5 sm:gap-2 mb-1">
                  <span className="text-3xl sm:text-5xl font-display font-normal text-white tracking-normal">₦2,500</span>
                  <span className="text-xs text-muted-foreground mb-1 sm:mb-2">/ month</span>
                </div>
                <p className="text-xs text-primary/80 font-medium mb-6 sm:mb-8">Or ₦25,000 / yr (₦5,000 saved yearly)</p>

                <ul className="space-y-2.5 sm:space-y-3 text-xs sm:text-sm text-white/90">
                  <li className="flex items-center gap-2.5 sm:gap-3">
                    <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                    <span>Up to <strong>10 forms</strong></span>
                  </li>
                  <li className="flex items-center gap-2.5 sm:gap-3">
                    <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                    <span><strong>500 submissions</strong> / month</span>
                  </li>
                  <li className="flex items-center gap-2.5 sm:gap-3">
                    <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                    <span>Advanced analytics</span>
                  </li>
                  <li className="flex items-center gap-2.5 sm:gap-3">
                    <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                    <span>Custom branding & logos</span>
                  </li>
                  <li className="flex items-center gap-2.5 sm:gap-3">
                    <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                    <span>CSV Data Export</span>
                  </li>
                  <li className="flex items-center gap-2.5 sm:gap-3">
                    <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                    <span>Priority email support</span>
                  </li>
                </ul>
              </div>

              <Button
                className="w-full h-11 sm:h-12 rounded-xl sm:rounded-2xl bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-[0_0_30px_rgba(0,210,180,0.4)] mt-6 sm:mt-8 text-sm sm:text-base"
                onClick={() => navigate('/auth/signup')}
              >
                Choose Starter
              </Button>
            </div>

            {/* Pro Plan */}
            <div className="bg-card/50 border border-white/10 rounded-2xl sm:rounded-3xl p-5 sm:p-8 lg:p-10 flex flex-col justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium mb-2 sm:mb-3">Pro</p>
                <div className="flex items-end gap-1.5 sm:gap-2 mb-1">
                  <span className="text-3xl sm:text-5xl font-display font-normal text-white tracking-normal">₦5,000</span>
                  <span className="text-xs text-muted-foreground mb-1 sm:mb-2">/ month</span>
                </div>
                <p className="text-xs text-muted-foreground mb-6 sm:mb-8">Or ₦50,000 / yr (₦10,000 saved yearly)</p>

                <ul className="space-y-2.5 sm:space-y-3 text-xs sm:text-sm text-muted-foreground">
                  <li className="flex items-center gap-2.5 sm:gap-3">
                    <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                    <span><strong>Unlimited forms</strong></span>
                  </li>
                  <li className="flex items-center gap-2.5 sm:gap-3">
                    <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                    <span><strong>Unlimited submissions</strong></span>
                  </li>
                  <li className="flex items-center gap-2.5 sm:gap-3">
                    <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                    <span>Built-in CRM & Contact records</span>
                  </li>
                  <li className="flex items-center gap-2.5 sm:gap-3">
                    <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                    <span>API Access & Webhooks</span>
                  </li>
                  <li className="flex items-center gap-2.5 sm:gap-3">
                    <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                    <span>White-label solution</span>
                  </li>
                  <li className="flex items-center gap-2.5 sm:gap-3">
                    <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                    <span>24/7 dedicated support</span>
                  </li>
                </ul>
              </div>

              <Button
                variant="outline"
                className="w-full h-11 sm:h-12 rounded-xl sm:rounded-2xl border-white/10 text-white hover:bg-white/5 font-semibold mt-6 sm:mt-8 text-sm sm:text-base"
                onClick={() => navigate('/auth/signup')}
              >
                Choose Pro
              </Button>
            </div>

          </div>

          {/* Enterprise Card */}
          <div className="bg-card/40 border border-white/10 rounded-2xl sm:rounded-3xl p-5 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 backdrop-blur-xl">
            <div className="text-left space-y-1">
              <span className="text-xs font-bold uppercase tracking-widest text-primary">Enterprise</span>
              <h3 className="text-2xl sm:text-3xl font-display font-normal text-white">Need a custom plan for large scale data collection?</h3>
              <p className="text-xs sm:text-sm text-muted-foreground">Custom form limits, dedicated account manager, SLA uptime guarantees, and custom API integrations.</p>
            </div>
            <Button
              variant="outline"
              className="w-full sm:w-auto rounded-full h-11 sm:h-12 px-6 sm:px-8 border-white/20 text-white hover:bg-white/10 text-sm sm:text-base font-semibold shrink-0"
              onClick={() => window.open('mailto:hello@bravaxltd.com', '_blank')}
            >
              Contact Sales
            </Button>
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="scroll-mt-24 py-10 sm:py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            {/* Left */}
            <div className="lg:sticky lg:top-32">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 sm:px-5 sm:py-2 rounded-full bg-card border border-white/5 mb-6 sm:mb-8">
                <Sparkles className="h-4 w-4 text-white/60" />
                <span className="text-xs sm:text-sm text-white/80 font-medium">FAQ</span>
              </div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-medium text-white leading-tight tracking-normal mb-4 sm:mb-6">
                Frequently asked<br className="hidden sm:inline" /> questions.
              </h2>
              <p className="text-muted-foreground text-sm sm:text-lg leading-relaxed">
                Everything you need to know about Bravax Forms. Can't find an answer?{' '}
                <a href="mailto:hello@bravaxltd.com" className="text-primary hover:underline">Get in touch.</a>
              </p>
            </div>

            {/* Right — Accordion */}
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div
                  key={i}
                  className="bg-card/50 border border-white/5 rounded-xl sm:rounded-2xl overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between p-4 sm:p-6 text-left gap-3 sm:gap-4 group"
                    aria-expanded={openFaq === i}
                  >
                    <span className="text-white font-sans font-semibold text-sm sm:text-base group-hover:text-primary transition-colors">{faq.q}</span>
                    <span className={`flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full border border-white/10 text-muted-foreground transition-transform duration-300 ${openFaq === i ? 'rotate-180 border-primary/30 text-primary' : ''}`}>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </span>
                  </button>
                  {openFaq === i && (
                    <div className="px-4 pb-4 sm:px-6 sm:pb-6 text-xs sm:text-sm text-muted-foreground leading-relaxed border-t border-white/5 pt-3 sm:pt-4">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-10 sm:py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center relative z-10">
          <div className="space-y-4 sm:space-y-6 mb-8 sm:mb-12">
            <p className="text-xs sm:text-sm font-medium text-primary uppercase tracking-[0.2em]">Ready when you are</p>
            <h2 className="text-3xl sm:text-6xl lg:text-8xl font-display text-white leading-tight sm:leading-[1.02] tracking-normal">
              A form shouldn't just<br />
              <span className="text-primary">collect. It should act.</span>
            </h2>
            <p className="text-sm sm:text-lg text-muted-foreground max-w-lg mx-auto px-2">
              Describe what you need to collect. Bravax Forms handles the form, the data, and what comes after.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 w-full sm:w-auto">
            <Button
              size="lg"
              onClick={() => navigate('/auth/signup')}
              className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-8 sm:px-12 h-12 sm:h-14 text-base sm:text-lg shadow-[0_0_40px_rgba(0,210,180,0.25)] transition-all hover:scale-[1.03] font-semibold"
            >
              Start Building
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button variant="ghost" className="w-full sm:w-auto text-white/60 hover:text-white hover:bg-white/5 rounded-full px-6 sm:px-8 h-12 sm:h-14 text-sm sm:text-base" onClick={() => navigate('/auth/login')}>
              Already have an account →
            </Button>
          </div>
          <p className="mt-4 sm:mt-6 text-xs text-muted-foreground/50 tracking-wide">No credit card · Free forever on the Free plan</p>
        </section>
      </main>

      {/* Footer */}
      <footer className="px-3 sm:px-6 lg:px-8 pb-6 sm:pb-8 relative z-10">
        <div className="max-w-[90rem] mx-auto rounded-[1.5rem] sm:rounded-[2.5rem] bg-surface-lowest border border-white/5 overflow-hidden relative">
          <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-primary/10 blur-[120px] rounded-full pointer-events-none" />

          {/* CTA strip */}
          <div className="relative px-5 sm:px-10 lg:px-16 py-8 sm:py-10 border-b border-white/5 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <p className="text-2xl sm:text-3xl font-display text-white leading-tight">
                Create smarter forms. <span className="italic text-white/60">Understand every response.</span>
              </p>
              <p className="mt-2 text-sm text-muted-foreground">Free plan available. No credit card required.</p>
            </div>
            <Button
              className="h-11 sm:h-12 px-6 rounded-full bg-primary text-primary-foreground font-semibold shadow-[0_0_30px_rgba(0,210,180,0.25)] hover:scale-[1.02] transition-all shrink-0 self-start md:self-auto"
              onClick={() => navigate('/auth/signup')}
            >
              Start Building <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>

          {/* Brand + link columns */}
          <div className="relative px-5 sm:px-10 lg:px-16 py-10 sm:py-14 grid grid-cols-2 md:grid-cols-12 gap-x-6 gap-y-10">
            <div className="col-span-2 md:col-span-5 lg:col-span-4 space-y-4">
              <div className="flex items-center gap-3">
                <img src={customLogo} alt="Bravax Forms" className="h-7 w-auto object-contain" />
                <span className="text-lg font-sans font-bold text-white leading-none">Bravax Forms</span>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed max-w-sm">
                The AI form builder that turns responses into contacts, insights and action, all from one place.
              </p>
            </div>

            {[
              {
                title: 'Product',
                links: [
                  { label: 'Features', href: '#features' },
                  { label: 'Pricing', href: '#pricing' },
                  { label: 'AI Builder', href: '/auth/signup' },
                  { label: 'FAQ', href: '#faq' },
                ],
              },
              {
                title: 'Resources',
                links: [
                  { label: 'Blog', href: '/blog' },
                  { label: 'API Docs', href: '/api-docs' },
                  { label: 'Sitemap', href: '/sitemap' },
                ],
              },
              {
                title: 'Company',
                links: [
                  { label: 'Bravax Technologies', href: 'https://bravaxltd.com', external: true },
                  { label: 'Privacy', href: '#' },
                  { label: 'Terms', href: '#' },
                ],
              },
            ].map((col, idx) => (
              <div key={col.title} className={`md:col-span-2 space-y-4 ${idx === 0 ? 'lg:col-start-7' : ''}`}>
                <p className="text-xs uppercase tracking-widest text-white/40 font-medium">{col.title}</p>
                <ul className="space-y-3 text-sm text-muted-foreground">
                  {col.links.map((link) => (
                    <li key={link.label}>
                      <a
                        href={link.href}
                        {...('external' in link && link.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                        className="hover:text-primary transition-colors"
                      >
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Bottom bar */}
          <div className="relative px-5 sm:px-10 lg:px-16 py-5 border-t border-white/5 flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-muted-foreground/60">
            <span>© {new Date().getFullYear()} Bravax Technologies Limited. All rights reserved.</span>
            <button
              type="button"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="inline-flex items-center gap-1.5 hover:text-white transition-colors"
            >
              Back to top <ArrowRight className="h-3.5 w-3.5 -rotate-90" />
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;

